import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import './App.css';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppFrame from './components/AppFrame';
import { seoRoutes, isSeoPath } from './seo/SeoRoutes';
// HIDDEN: Shortlists functionality moved to Compare page - see docs/architecture/compare_export_feature.md
// import ShortlistsPage from './pages/ShortlistsPage';
import './Product.css';
import './seo/seo.css';

// These routes include map/chart libraries that static SEO pages never use.
// Defer each route independently so the shared SEO tree hydrates unchanged.
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const ValuationPage = lazy(() => import('./pages/ValuationPage'));
const PreListingValuationPage = lazy(() => import('./pages/PreListingValuationPage'));
const AgentProfilePage = lazy(() => import('./pages/AgentProfilePage'));

function deferredPage(Page) {
  return (
    <Suspense fallback={<main className="product-page" aria-busy="true"><p role="status">Loading page…</p></main>}>
      <Page />
    </Suspense>
  );
}

// Dev-only fixture previews for the static SEO pages. The condition is a
// build-time constant, so production bundles never include the fixtures.
const SeoPreviewPage =
  process.env.NODE_ENV !== 'production' ? lazy(() => import('./seo/preview/SeoPreviewPage')) : null;

function readStoredTheme() {
  try {
    return localStorage.getItem('theme') || 'light';
  } catch (error) {
    return 'light';
  }
}

// Prerendered pages were rendered on the server in the light theme; the
// first client render must match, so the stored theme is applied after.
const isPrerendered =
  typeof document !== 'undefined' && document.getElementById('root')?.dataset.prerendered === 'true';

/**
 * App serves as the root component for the RealEstats frontend.  It
 * defines the high‑level routes and manages the global colour theme.  A
 * persistent navigation bar is rendered at the top of every page.
 */
export default function App() {
  const location = useLocation();

  // Persist theme between sessions.  Use localStorage for simplicity.
  const [theme, setTheme] = useState(() => (isPrerendered ? 'light' : readStoredTheme()));
  const themeSynced = useRef(!isPrerendered);

  useEffect(() => {
    if (!themeSynced.current) {
      themeSynced.current = true;
      const stored = readStoredTheme();
      if (stored !== theme) {
        setTheme(stored);
        return;
      }
    }
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    try {
      localStorage.setItem('theme', theme);
    } catch (error) {
      // Storage can be unavailable (private mode); the theme still applies.
    }
  }, [theme]);

  useEffect(() => {
    // The static data pages set their own titles from page data.
    if (isSeoPath(location.pathname) || location.pathname.startsWith('/__preview')) return;
    const routeTitle = location.pathname.startsWith('/compare')
      ? 'Compare Sydney property markets | RealEstats'
      : location.pathname.startsWith('/insights')
        ? 'Sydney market insights | RealEstats'
        : location.pathname.startsWith('/leaderboard')
          ? 'Agent guide gap leaderboard | RealEstats'
          : location.pathname.startsWith('/valuation')
            ? 'Property valuation tools | RealEstats'
            : location.pathname.startsWith('/agent/')
              ? 'Agent guide gap profile | RealEstats'
              : 'Explore Sydney property data | RealEstats';
    document.title = routeTitle;
  }, [location.pathname]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <AppFrame theme={theme} onToggleTheme={toggleTheme}>
      <Routes>
        <Route path="/" element={<Navigate to="/explore" replace />} />
        <Route path="/explore" element={deferredPage(ExplorePage)} />
        <Route path="/compare" element={deferredPage(ComparePage)} />
        {/* HIDDEN: Shortlists route disabled - export functionality moved to Compare page */}
        {/* <Route path="/shortlists" element={<ShortlistsPage />} /> */}
        <Route path="/insights" element={deferredPage(InsightsPage)} />
        <Route path="/leaderboard" element={deferredPage(LeaderboardPage)} />
        <Route path="/agent/:agentId" element={deferredPage(AgentProfilePage)} />
        <Route path="/valuation" element={deferredPage(ValuationPage)} />
        <Route path="/valuation/prelisting" element={deferredPage(PreListingValuationPage)} />
        <Route path="/newsletters/*" element={<Navigate to="/insights" replace />} />
        {seoRoutes}
        {SeoPreviewPage ? (
          <Route
            path="/__preview/*"
            element={
              <Suspense fallback={null}>
                <SeoPreviewPage />
              </Suspense>
            }
          />
        ) : null}
        {/* Fallback to /explore for unknown routes */}
        <Route path="*" element={<Navigate to="/explore" replace />} />
      </Routes>
    </AppFrame>
  );
}
